import { useState, useEffect } from 'react';
import {
	Box,
	IconButton,
	Divider,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	Tooltip,
	Menu,
	MenuItem,
	CircularProgress,
	useTheme,
	useMediaQuery,
	alpha,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InsightsIcon from '@mui/icons-material/Insights';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext, ACTION_TYPES } from '../Context/AppContext';
import {
	logoutUser,
	makeApiRequest,
	storeActiveOrganization,
} from '../Shared/Api';
import { getAuthenticatedHomePath } from '../Shared/homePath';
import { APP_NAME } from '../Shared/branding';
import { glassSurface, glassPill } from '../Shared/glassStyles';
import Logo from './Logo';
import OrgAvatar from './OrgAvatar';

const drawerWidthExpanded = 248;
const drawerWidthCollapsed = 76;
const navMargin = 16;

const Sidebar = ({
	mobileOpen,
	onMobileClose,
	toggleDarkMode,
	darkMode,
	sidebarOpen: sidebarOpenProp,
	onSidebarOpenChange,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [sidebarOpenInternal, setSidebarOpenInternal] = useState(() => {
		const saved = localStorage.getItem('sidebarOpen');
		return saved !== null ? JSON.parse(saved) : true;
	});

	const sidebarOpen = sidebarOpenProp ?? sidebarOpenInternal;
	const setSidebarOpen = onSidebarOpenChange ?? setSidebarOpenInternal;

	const [selectedTab, setSelectedTab] = useState('jobs');
	const navigate = useNavigate();
	const location = useLocation();
	const { state, dispatch } = useAppContext();
	const organization = state.user.organization;
	const organizationName = organization?.name;
	const activeOrganizationId = organization?.id;
	const isSuperuser = state.user.isSuperuser;
	const showOrgSection = Boolean(organizationName) || isSuperuser;
	const orgDisplayName = organizationName || 'Select organization';
	const isManager = state.user.role === 'Manager' || isSuperuser;
	const hasTeamAccounts = Boolean(organization?.features?.team_accounts);
	const hasScheduling = Boolean(organization?.features?.scheduling);
	const hasTimeTracking = Boolean(organization?.features?.time_tracking);
	const hasAnalytics = Boolean(organization?.features?.analytics);
	const homePath = getAuthenticatedHomePath(state.user);

	const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
	const [organizations, setOrganizations] = useState([]);
	const [orgsLoading, setOrgsLoading] = useState(false);

	const isExpanded = sidebarOpen || isMobile;
	const drawerWidth = isExpanded ? drawerWidthExpanded : drawerWidthCollapsed;

	useEffect(() => {
		if (!isMobile) {
			localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
		}
	}, [sidebarOpen, isMobile]);

	const toggleSidebar = () => {
		setSidebarOpen(!sidebarOpen);
	};

	const handleOrgMenuOpen = async (event) => {
		if (!isSuperuser) {
			return;
		}
		setOrgMenuAnchor(event.currentTarget);
		setOrgsLoading(true);
		try {
			const response = await makeApiRequest('GET', '/auth/organizations/');
			setOrganizations(response || []);
		} catch (error) {
			console.error('Failed to fetch organizations:', error);
		} finally {
			setOrgsLoading(false);
		}
	};

	const handleOrgMenuClose = () => {
		setOrgMenuAnchor(null);
	};

	const handleOrgSelect = (org) => {
		if (!org?.id) {
			return;
		}
		storeActiveOrganization(org);
		dispatch({
			type: ACTION_TYPES.SET_ACTIVE_ORGANIZATION,
			payload: org,
		});
		handleOrgMenuClose();
		if (location.pathname.startsWith('/jobs/view')) {
			navigate(homePath);
		}
	};

	const handleLogout = async () => {
		try {
			await logoutUser();
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			dispatch({ type: ACTION_TYPES.LOGGED_OUT });
			navigate('/login');
		}
	};

	useEffect(() => {
		const currentPath = location.pathname.split('/')[1];
		setSelectedTab(currentPath || 'jobs');
	}, [location]);

	const menuItems = [
		...(isManager
			? [{ id: 'jobs', title: 'Jobs', icon: <WorkIcon /> }]
			: []),
		...(hasScheduling
			? [{ id: 'schedule', title: 'Schedule', icon: <CalendarMonthIcon /> }]
			: []),
		...(hasTimeTracking
			? [{ id: 'time', title: 'Time', icon: <AccessTimeIcon /> }]
			: []),
		...(hasAnalytics && isManager
			? [{ id: 'analytics', title: 'Analytics', icon: <InsightsIcon /> }]
			: []),
		...(isManager
			? [{ id: 'customers', title: 'Customers', icon: <PeopleIcon /> }]
			: []),
		...(hasTeamAccounts && (isManager || isSuperuser)
			? [{ id: 'users', title: 'Users', icon: <PersonAddIcon /> }]
			: []),
		...(isManager
			? [{ id: 'settings', title: 'Settings', icon: <SettingsIcon /> }]
			: []),
		...(isSuperuser
			? [
					{
						id: 'organizations',
						title: 'Organizations',
						icon: <BusinessIcon />,
					},
				]
			: []),
	];

	const glassPanelSx = {
		...glassSurface(theme, 'strong'),
		borderRadius: '24px',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
	};

	const drawerContent = (
		<Box sx={glassPanelSx}>
			{/* Logo header */}
			<Box
				sx={{
					flexShrink: 0,
					px: isExpanded ? 2 : 1,
					py: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: isExpanded ? 'space-between' : 'center',
					minHeight: 64,
				}}
			>
				{isExpanded ? (
					<>
						<Box
							component="button"
							type="button"
							onClick={() => navigate(homePath)}
							aria-label={APP_NAME}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1.25,
								cursor: 'pointer',
								border: 'none',
								background: 'none',
								padding: 0,
								font: 'inherit',
								color: 'inherit',
								minWidth: 0,
								flex: 1,
								transition: 'opacity 0.2s',
								'&:hover': { opacity: 0.8 },
							}}
						>
							<Logo sx={{ height: 26, width: 'auto', flexShrink: 0 }} />
							<Typography
								sx={{
									fontWeight: 700,
									fontSize: '1.05rem',
									letterSpacing: '-0.03em',
									lineHeight: 1.1,
									color: 'text.primary',
									textTransform: 'lowercase',
									minWidth: 0,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{APP_NAME}
							</Typography>
						</Box>
						{!isMobile && (
							<Tooltip title="Collapse" placement="right">
								<IconButton
									onClick={toggleSidebar}
									size="small"
									sx={{
										color: 'text.secondary',
										flexShrink: 0,
										...glassPill(theme, false),
										borderRadius: '12px',
										width: 32,
										height: 32,
									}}
								>
									<ChevronLeftIcon sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
						)}
					</>
				) : (
					<Tooltip title={APP_NAME} placement="right">
						<Box
							component="button"
							type="button"
							onClick={() => navigate(homePath)}
							aria-label={APP_NAME}
							sx={{
								border: 'none',
								background: 'none',
								p: 0.75,
								cursor: 'pointer',
								borderRadius: '14px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'transform 0.2s',
								'&:hover': { transform: 'scale(1.05)' },
							}}
						>
							<Logo sx={{ height: 32, width: 32, objectFit: 'contain' }} />
						</Box>
					</Tooltip>
				)}
			</Box>

			{/* Navigation */}
			<List sx={{ flexGrow: 1, px: 1.25, py: 0.5, overflowY: 'auto' }}>
				{menuItems.map((item) => {
					const isActive = selectedTab === item.id;
					return (
						<Tooltip
							key={item.id}
							title={!isExpanded ? item.title : ''}
							placement="right"
						>
							<ListItem disablePadding sx={{ mb: 0.5 }}>
								<ListItemButton
									selected={isActive}
									onClick={() => {
										navigate(`/${item.id}`);
										setSelectedTab(item.id);
										if (isMobile) onMobileClose();
									}}
									sx={{
										borderRadius: '14px',
										minHeight: 44,
										justifyContent: isExpanded ? 'initial' : 'center',
										px: isExpanded ? 1.75 : 1,
										py: 1,
										transition: theme.transitions.create(
											['background', 'box-shadow', 'transform'],
											{ duration: 220 },
										),
										...glassPill(theme, isActive),
										'&.Mui-selected': {
											color: 'primary.main',
											'& .MuiListItemIcon-root': {
												color: 'primary.main',
											},
										},
										'&:active': {
											transform: 'scale(0.97)',
										},
									}}
								>
									<ListItemIcon
										sx={{
											minWidth: 0,
											mr: isExpanded ? 1.5 : 0,
											justifyContent: 'center',
											color: isActive ? 'primary.main' : 'text.secondary',
											transition: 'color 0.2s',
											'& .MuiSvgIcon-root': { fontSize: 22 },
										}}
									>
										{item.icon}
									</ListItemIcon>
									{isExpanded && (
										<ListItemText
											primary={item.title}
											primaryTypographyProps={{
												fontSize: '0.9rem',
												fontWeight: isActive ? 600 : 500,
												letterSpacing: '-0.01em',
											}}
										/>
									)}
								</ListItemButton>
							</ListItem>
						</Tooltip>
					);
				})}
			</List>

			<Divider sx={{ opacity: 0.4, mx: 2 }} />

			{/* Bottom actions */}
			<Box
				sx={{
					px: 1.25,
					py: 1.5,
					display: 'flex',
					flexDirection: isExpanded ? 'row' : 'column',
					alignItems: 'center',
					justifyContent: isExpanded ? 'center' : 'stretch',
					gap: 0.75,
				}}
			>
				<Tooltip
					title={darkMode ? 'Light mode' : 'Dark mode'}
					placement={isExpanded ? 'top' : 'right'}
				>
					<IconButton
						onClick={toggleDarkMode}
						size="small"
						sx={{
							color: 'text.secondary',
							borderRadius: '12px',
							width: 36,
							height: 36,
							...glassPill(theme, false),
						}}
					>
						{darkMode ? (
							<LightModeIcon sx={{ fontSize: 20 }} />
						) : (
							<DarkModeIcon sx={{ fontSize: 20 }} />
						)}
					</IconButton>
				</Tooltip>

				<Tooltip
					title="Sign out"
					placement={isExpanded ? 'top' : 'right'}
				>
					<IconButton
						onClick={handleLogout}
						size="small"
						sx={{
							color: 'text.secondary',
							borderRadius: '12px',
							width: 36,
							height: 36,
							...glassPill(theme, false),
						}}
					>
						<LogoutIcon sx={{ fontSize: 20 }} />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Organization */}
			{showOrgSection && (
				<>
					<Tooltip
						title={
							!isExpanded
								? isSuperuser
									? organizationName
										? `${organizationName} — switch`
										: 'Select organization'
									: organizationName
								: isSuperuser
									? organizationName
										? 'Switch organization'
										: 'Select organization'
									: ''
						}
						placement="right"
					>
						<Box
							component={isSuperuser ? 'button' : 'div'}
							type={isSuperuser ? 'button' : undefined}
							onClick={isSuperuser ? handleOrgMenuOpen : undefined}
							sx={{
								mx: 1.25,
								mb: 1.25,
								px: isExpanded ? 1.5 : 1,
								py: 1.25,
								borderRadius: '16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: isExpanded ? 'flex-start' : 'center',
								gap: 1.25,
								minHeight: 48,
								cursor: isSuperuser ? 'pointer' : 'default',
								textAlign: 'left',
								font: 'inherit',
								color: 'inherit',
								border: 'none',
								...glassPill(theme, false),
								...(isSuperuser && {
									'&:hover': {
										background: alpha(
											theme.palette.primary.main,
											theme.palette.mode === 'dark' ? 0.1 : 0.06,
										),
									},
								}),
							}}
						>
							<OrgAvatar organization={organization} size={30} />
							{isExpanded && (
								<Box sx={{ minWidth: 0, flex: 1 }}>
									<Typography
										variant="caption"
										sx={{
											display: 'block',
											color: 'text.secondary',
											fontSize: '0.6rem',
											fontWeight: 600,
											textTransform: 'uppercase',
											letterSpacing: '0.08em',
											lineHeight: 1.2,
											mb: 0.25,
										}}
									>
										Organization
									</Typography>
									<Typography
										variant="body2"
										noWrap
										sx={{
											fontWeight: 600,
											lineHeight: 1.3,
											fontSize: '0.85rem',
											color: organizationName
												? 'text.primary'
												: 'text.secondary',
										}}
									>
										{orgDisplayName}
									</Typography>
								</Box>
							)}
							{isSuperuser && isExpanded && (
								<UnfoldMoreIcon
									sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }}
								/>
							)}
						</Box>
					</Tooltip>
					{isSuperuser && (
						<Menu
							anchorEl={orgMenuAnchor}
							open={Boolean(orgMenuAnchor)}
							onClose={handleOrgMenuClose}
							anchorOrigin={{
								vertical: 'top',
								horizontal: isExpanded ? 'right' : 'center',
							}}
							transformOrigin={{
								vertical: 'bottom',
								horizontal: isExpanded ? 'left' : 'center',
							}}
							slotProps={{
								paper: {
									sx: {
										minWidth: 240,
										maxWidth: 300,
										borderRadius: '16px',
										...glassSurface(theme, 'strong'),
										mt: -1,
									},
								},
							}}
						>
							{orgsLoading ? (
								<MenuItem disabled sx={{ justifyContent: 'center' }}>
									<CircularProgress size={20} />
								</MenuItem>
							) : organizations.length === 0 ? (
								<MenuItem disabled>No organizations found</MenuItem>
							) : (
								organizations.map((org) => (
									<MenuItem
										key={org.id}
										selected={org.id === activeOrganizationId}
										onClick={() => handleOrgSelect(org)}
										sx={{
											gap: 1,
											borderRadius: '10px',
											mx: 0.75,
											my: 0.25,
										}}
									>
										<ListItemIcon sx={{ minWidth: 28 }}>
											{org.id === activeOrganizationId ? (
												<CheckIcon fontSize="small" color="primary" />
											) : (
												<Box sx={{ width: 20 }} />
											)}
										</ListItemIcon>
										<ListItemText
											primary={org.name}
											primaryTypographyProps={{
												noWrap: true,
												fontWeight:
													org.id === activeOrganizationId ? 600 : 400,
												fontSize: '0.9rem',
											}}
										/>
									</MenuItem>
								))
							)}
						</Menu>
					)}
				</>
			)}
		</Box>
	);

	if (isMobile) {
		return (
			<Drawer
				variant="temporary"
				open={mobileOpen}
				onClose={onMobileClose}
				ModalProps={{ keepMounted: true }}
				sx={{
					display: { xs: 'block', md: 'none' },
					'& .MuiDrawer-paper': {
						boxSizing: 'border-box',
						width: drawerWidthExpanded + navMargin * 2,
						bgcolor: 'transparent',
						border: 'none',
						boxShadow: 'none',
						p: `${navMargin}px`,
					},
					'& .MuiBackdrop-root': {
						backdropFilter: 'blur(4px)',
					},
				}}
			>
				{drawerContent}
			</Drawer>
		);
	}

	return (
		<>
			<Box
				sx={{
					position: 'fixed',
					top: navMargin,
					left: navMargin,
					bottom: navMargin,
					width: drawerWidth,
					zIndex: theme.zIndex.drawer,
					transition: theme.transitions.create('width', {
						easing: theme.transitions.easing.easeInOut,
						duration: theme.transitions.duration.enteringScreen,
					}),
				}}
			>
				{drawerContent}
			</Box>

			{!sidebarOpen && (
				<Tooltip title="Expand navigation" placement="right">
					<IconButton
						onClick={toggleSidebar}
						size="small"
						aria-label="Expand navigation"
						sx={{
							position: 'fixed',
							left: navMargin + drawerWidthCollapsed - 12,
							top: navMargin + 20,
							zIndex: theme.zIndex.drawer + 1,
							width: 24,
							height: 24,
							...glassSurface(theme, 'medium'),
							borderRadius: '50%',
							color: 'text.secondary',
							'&:hover': { color: 'text.primary' },
						}}
					>
						<ChevronRightIcon sx={{ fontSize: 16 }} />
					</IconButton>
				</Tooltip>
			)}
		</>
	);
};

export default Sidebar;

export { drawerWidthExpanded, drawerWidthCollapsed, navMargin };
